<template>
  <div>
    <a-modal
      v-model="visible"
      :title="`Edit timetable entry for ${day}`"
      style="top: 20px;"
      @cancel="handleCancel"
      :destroyOnClose="false"
    >
      <template slot="footer">
        <a-button key="back" @click="handleCancel">Cancel</a-button>
        <a-button key="submit" type="primary" :loading="loading" @click="handleSubmit">Update</a-button>
      </template>

      <a-spin :spinning="formLoading">
        <a-form :form="form" @submit="handleSubmit">
          <div class="row mb-0">
            <!-- Start time -->
            <div class="col-md-4 mb-2">
              <a-form-item label="Start Time">
                <a-time-picker
                  :open="open"
                  @openChange="handleTimePickerOpenChange"
                  v-decorator="[
                    'start_time',
                    {rules: [{ required: true, message: 'Please input start time' }]},
                  ]"
                  use12Hours
                  :format="timeFormat"
                >
                  <a-button slot="addon" size="small" type="primary" @click="handleTimePickerClose">
                    Ok
                  </a-button>
                </a-time-picker>
              </a-form-item>
            </div>
            <!-- End time -->
            <div class="col-md-4 mb-2">
              <a-form-item label="End Time">
                <a-time-picker
                  :open.sync="open2"
                  v-decorator="[
                    'end_time',
                    {rules: [{ required: true, message: 'Please input end time' }]},
                  ]"
                  use12Hours
                  :format="timeFormat"
                >
                  <a-button slot="addon" size="small" type="primary" @click="handleTimePickerClose">
                    Ok
                  </a-button>
                </a-time-picker>
              </a-form-item>
            </div>
            <!-- Select Type -->
            <div class="col-md-4 mb-2">
              <a-form-item label="Type">
                <a-select
                  width="100%"
                  v-decorator="[
                    'type',
                    {rules: [{ required: true, message: 'Please select a type!' }]},
                  ]"
                  @change="generatedValue"
                >
                  <a-select-option :value="0">Normal</a-select-option>
                  <a-select-option :value="1">Break</a-select-option>
                  <a-select-option :value="2">Off</a-select-option>
                  <a-select-option :value="3">Assembly</a-select-option>
                  <a-select-option :value="9">Custom</a-select-option>
                </a-select>
              </a-form-item>
            </div>

            <div class="col-12 mb-2">
              <a-form-item
                label="Type Value"
                :hidden="!form.getFieldValue('type')"
              >
                <a-input
                  placeholder="Type value"
                  width="100%"
                  v-decorator="['type_value']"
                  :hidden="!form.getFieldValue('type')"
                />
              </a-form-item>
            </div>

            <div class="col-12 mb-2" v-if="!form.getFieldValue('type')">
              <p class="mb-0 py-2">
                <a-checkbox
                  :checked="isMultiSubject"
                  @change="onMultiSubjectChange"
                >
                  Select multiple subjects other than Electives or Subject Groups
                </a-checkbox>
              </p>
            </div>

            <template v-if="!isMultiSubject && !form.getFieldValue('type')">
              <!-- Select class -->
              <div class="col-md-6 mb-2">
                <a-form-item label="Class">
                  <a-select
                    placeholder="Select Class"
                    width="100%"
                    v-decorator="[
                      'class_id',
                      { rules: [{ required: true, message: 'Please select a class!' }] }
                    ]"
                    @change="handleClassChange"
                  >
                    <a-select-option :value="undefined">Select Class</a-select-option>
                    <a-select-option
                      v-for="cls in classes"
                      :key="cls.id"
                      :value="cls.id"
                    >
                      {{ cls.name }}
                    </a-select-option>
                  </a-select>
                </a-form-item>
              </div>

              <!-- Select Section -->
              <div class="col-md-6 mb-2">
                <a-form-item label="Section">
                  <a-select
                    placeholder="Select Section"
                    width="100%"
                    v-decorator="[
                      'section_id',
                      { rules: [{ required: true, message: 'Please select a section!' }] }
                    ]"
                  >
                    <a-select-option :value="undefined">Select Section</a-select-option>
                    <a-select-option
                      v-for="sec in sections"
                      :key="sec.id"
                      :value="sec.id"
                    >
                      {{ sec.name }}
                    </a-select-option>
                  </a-select>
                </a-form-item>
              </div>

              <div class="col-md-6 mb-2">
                <a-form-item label="Subject">
                  <a-select
                    placeholder="Select Subject"
                    width="100%"
                    v-decorator="[
                      'subject_id',
                      { rules: [{ required: true, message: 'Please select a subject!' }] }
                    ]"
                    :loading="subjectSelectSpinning"
                    @change="handleSubjectSelect"
                  >
                    <a-select-option :value="undefined">Select Subject</a-select-option>
                    <a-select-option
                      v-for="subject in classSubjects.all"
                      :key="subject.id"
                      :value="subject.subject.id"
                    >
                      {{ subject.subject.name }}
                    </a-select-option>
                  </a-select>
                </a-form-item>
              </div>

              <!-- If electives -->
              <div class="col-md-12 mb-2" v-if="electiveSubjects.length">
                <p class="font-weight-bold text-dark mb-0">Electives:</p>
                <table class="table table-sm table-borderless mb-0">
                  <tbody>
                    <tr v-for="electiveSubject in electiveSubjects" :key="electiveSubject.subject_id">
                      <th scope="row" style="vertical-align:middle;width: 50%;">
                        {{ electiveSubject.subject_name }} ({{ electiveSubject.abbreviation }})
                        <a-form-item class="d-none">
                          <a-input
                            v-decorator="[
                              `subject_teachers[${electiveSubject.subject_id}]subject_id`,
                              { initialValue: electiveSubject.subject_id },
                            ]"
                          />
                        </a-form-item>
                      </th>
                      <td scope="col" style="width: 50%;" class="p-0">
                        <a-form-item>
                          <a-select
                            placeholder="Select Teacher"
                            width="100%"
                            v-decorator="[
                              `subject_teachers[${electiveSubject.subject_id}]teacher_id`,
                              { rules: [{ required: true, message: 'Please select a teacher!' }] },
                            ]"
                            :loading="teacherSelectSpinning"
                          >
                            <a-select-option :value="undefined">Select Teacher</a-select-option>
                            <a-select-option
                              v-for="teacher in teachers"
                              :value="teacher.id"
                              :key="teacher.id"
                            >
                              {{ teacher.first_name }} {{ teacher.last_name }}
                            </a-select-option>
                          </a-select>
                        </a-form-item>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <!-- If group -->
              <div class="col-md-12 mb-2" v-if="groupSubjects.length">
                <p class="font-weight-bold text-dark mb-0">Group Subjects:</p>
                <table class="table table-sm table-borderless mb-0">
                  <tbody>
                    <tr v-for="groupSubject in groupSubjects" :key="groupSubject.subject_id">
                      <th scope="row" style="vertical-align:middle;width: 50%;">
                        {{ groupSubject.subject_name }} ({{ groupSubject.abbreviation }})
                        <a-form-item class="d-none">
                          <a-input
                            v-decorator="[
                              `subject_teachers[${groupSubject.subject_id}]subject_id`,
                              { initialValue: groupSubject.subject_id },
                            ]"
                          />
                        </a-form-item>
                      </th>
                      <td scope="col" style="width: 50%;" class="p-0">
                        <a-form-item>
                          <a-select
                            placeholder="Select Teacher"
                            width="100%"
                            v-decorator="[
                              `subject_teachers[${groupSubject.subject_id}]teacher_id`,
                              { rules: [{ required: true, message: 'Please select a teacher!' }] },
                            ]"
                            :loading="teacherSelectSpinning"
                          >
                            <a-select-option :value="undefined">Select Teacher</a-select-option>
                            <a-select-option
                              v-for="teacher in teachers"
                              :value="teacher.id"
                              :key="teacher.id"
                            >
                              {{ teacher.first_name }} {{ teacher.last_name }}
                            </a-select-option>
                          </a-select>
                        </a-form-item>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </template>

            <template v-if="isMultiSubject && !form.getFieldValue('type')">
              <!-- If multi subject -->
              <div class="col-md-12">
                <p class="font-weight-bold text-dark mb-0">Multi Subjects:</p>
                <table class="table table-sm table-borderless mb-0">
                  <tbody>
                    <tr v-for="(entry, index) in multiSubjectEntries" :key="index">
                      <th style="vertical-align:middle;width: 50%;" class="p-0 pr-md-2">
                        <a-form-item>
                          <a-select
                            placeholder="Select Subject"
                            width="100%"
                            v-decorator="[
                              `subject_teachers[${index}]subject_id`,
                              { rules: [{ required: true, message: 'Please select a subject!' }] },
                            ]"
                            :loading="subjectSelectSpinning"
                            @change="handleSubjectSelect($event, index + 1)"
                          >
                            <a-select-option :value="undefined">Select Subject</a-select-option>
                            <a-select-option
                              v-for="subject in classSubjects.all"
                              :value="subject.subject.id"
                              :key="subject.id"
                            >
                              {{ subject.subject.name }}
                            </a-select-option>
                          </a-select>
                        </a-form-item>
                      </th>
                      <td style="vertical-align:middle;width: 50%;" class="p-0">
                        <a-form-item>
                          <a-select
                            placeholder="Select Teacher"
                            width="100%"
                            v-decorator="[
                              `subject_teachers[${index}]teacher_id`,
                              { rules: [{ required: true, message: 'Please select a teacher!' }] },
                            ]"
                            :loading="teacherSelectSpinning"
                          >
                            <a-select-option :value="undefined">Select Teacher</a-select-option>
                            <a-select-option
                              v-for="teacher in teachers"
                              :value="teacher.id"
                              :key="teacher.id"
                            >
                              {{ teacher.first_name }} {{ teacher.last_name }}
                            </a-select-option>
                          </a-select>
                        </a-form-item>
                      </td>
                    </tr>
                  </tbody>
                </table>
                <div class="pt-4">
                  <a-button
                    type="dashed"
                    @click="handleAddMoreSubject"
                    :disabled="multiSubjectEntries.length >= 6"
                    class="mr-2"
                  >
                    <a-icon type="plus" /> Add Subject
                  </a-button>
                  <a-button
                    type="dashed"
                    danger
                    @click="handleRemoveSubject"
                    :disabled="multiSubjectEntries.length <= 2"
                  >
                    <a-icon type="minus" /> Remove Subject
                  </a-button>
                </div>
              </div>
            </template>
          </div>
        </a-form>
      </a-spin>
    </a-modal>
  </div>
</template>

<script>
import moment from 'moment'
import { mapValues } from 'lodash'

export default {
  name: 'EditTeacherTimetableEntry',
  mounted: function() {
    this.$eventBus.$on('openTeacherEditEntryModal', this.showModal)
  },

  data: function() {
    return {
      form: this.$form.createForm(this),
      loading: false,
      formLoading: false,
      visible: false,
      teacherId: undefined,
      classId: undefined,
      sectionId: undefined,
      sessionId: undefined,
      timeFormat: 'h:mm a',
      position: undefined,
      day: undefined,
      open: false,
      open2: false,
      isMultiSubject: false,
      electiveGroupSubjectIds: [],
      electiveSubjects: [],
      groupSubjectIds: [],
      groupSubjects: [],
      teachers: [],
      classSubjects: {
        normal_subjects: [],
        elective_subjects: [],
        group_subjects: [],
        all: [],
      },
      classes: [],
      sections: [],
      subjectSelectSpinning: false,
      teacherSelectSpinning: false,
      typeValue: null,
      timetableId: null,
      originalData: null,
      originalGroupType: 0,
      multiSubjectEntries: [],
    }
  },

  methods: {
    moment,

    generatedValue(value) {
      switch (value) {
        case 0:
          this.typeValue = null
          break
        case 1:
          this.typeValue = 'Break'
          break
        case 2:
          this.typeValue = 'Off'
          break
        case 3:
          this.typeValue = 'Assembly'
          break
        case 9:
          this.typeValue = 'Custom'
          break
        default:
          this.typeValue = null
      }

      this.form.setFieldsValue({
        type_value: this.typeValue,
      })
    },

    showModal(data) {
      console.log('Teacher Edit Modal opened with data:', data)
      this.visible = true
      this.timetableId = data.id
      this.originalData = data
      this.loadTimetableData(data)
    },

    async loadTimetableData(data) {
      this.formLoading = true

      try {
        // Set basic data
        this.sessionId = data.session_id
        this.classId = data.class_id
        this.sectionId = data.section_id
        this.position = data.position
        this.day = data.day
        this.originalGroupType = data.group_type || 0

        // Load classes and teachers
        await this.getClasses()
        await this.getTeachers()

        // Load class subjects if class exists
        if (this.classId) {
          await this.getClassSubjects(this.classId)

          // Load sections for the class
          await this.$http.get(`/api/section/class/${this.classId}`)
            .then(({ data }) => {
              this.sections = data.data
            })
        }

        // Setup form values
        this.setupFormValues(data)

        this.formLoading = false
      } catch (error) {
        console.error('Error loading timetable data:', error)
        this.formLoading = false
        this.$neoNotify.error(error)
      }
    },

    setupFormValues(data) {
      const formValues = {
        start_time: moment(data.start_time, 'HH:mm:ss'),
        end_time: moment(data.end_time, 'HH:mm:ss'),
        type: data.type || 0,
        type_value: data.type_value || null,
        class_id: data.class_id || undefined,
        section_id: data.section_id || undefined,
        subject_id: data.subject_id || undefined,
        teacher_id: data.teacher_id || undefined,
      }

      // Handle different group types
      if (data.type === 0) {
        if (data.group_type === 1 || data.group_type === 2) {
          // Handle electives or groups
          this.isMultiSubject = false
          this.loadGroupEntries(data)
        } else if (data.group_type === 3) {
          // Handle multi-subjects
          this.isMultiSubject = true
          this.loadMultiSubjectEntries(data)
        } else {
          // Normal subject
          this.isMultiSubject = false
        }
      }

      this.$nextTick(() => {
        this.form.setFieldsValue(formValues)
        this.generatedValue(data.type || 0)
      })
    },

    loadGroupEntries(data) {
      if (data.group_subject_entries && data.group_subject_entries.length) {
        const entries = data.group_subject_entries
        if (data.group_type === 1) {
          this.electiveSubjects = entries.map(entry => ({
            subject_id: entry.subject.id,
            subject_name: entry.subject.name,
            abbreviation: entry.subject.abbreviation,
          }))
        } else if (data.group_type === 2) {
          this.groupSubjects = entries.map(entry => ({
            subject_id: entry.subject.id,
            subject_name: entry.subject.name,
            abbreviation: entry.subject.abbreviation,
          }))
        }

        // Set teacher values for each entry
        setTimeout(() => {
          const teacherValues = {}
          entries.forEach((entry) => {
            if (entry.teacher && entry.teacher.id) {
              teacherValues[`subject_teachers[${entry.subject.id}]teacher_id`] = entry.teacher.id
            }
            teacherValues[`subject_teachers[${entry.subject.id}]subject_id`] = entry.subject.id
          })
          this.form.setFieldsValue(teacherValues)
        }, 100)
      }
    },

    loadMultiSubjectEntries(data) {
      if (data.group_subject_entries && data.group_subject_entries.length) {
        this.multiSubjectEntries = data.group_subject_entries.map((entry, index) => ({
          index,
          subject_id: entry.subject.id,
          teacher_id: entry.teacher ? entry.teacher.id : null,
        }))

        // Set form values for each subject-teacher pair
        setTimeout(() => {
          const formValues = {}
          data.group_subject_entries.forEach((entry, index) => {
            formValues[`subject_teachers[${index}]subject_id`] = entry.subject.id
            if (entry.teacher && entry.teacher.id) {
              formValues[`subject_teachers[${index}]teacher_id`] = entry.teacher.id
            }
          })
          this.form.setFieldsValue(formValues)
        }, 100)
      }
    },

    getTeachers() {
      return new Promise((resolve, reject) => {
        this.teacherSelectSpinning = true
        this.$http.get('/api/teacher', { params: { limit: 100 } })
          .then(({ data }) => {
            this.teachers = data.data
            this.teacherSelectSpinning = false
            resolve()
          })
          .catch(error => {
            this.teacherSelectSpinning = false
            reject(error)
          })
      })
    },

    getClasses() {
      return new Promise((resolve, reject) => {
        this.$http.get('/api/class')
          .then(({ data }) => {
            this.classes = data.data
            resolve()
          })
          .catch(reject)
      })
    },

    getClassSubjects(classId) {
      return new Promise((resolve, reject) => {
        if (!classId || !this.sessionId) {
          resolve()
          return
        }

        this.subjectSelectSpinning = true
        this.$http.get(`/api/subject/class/${classId}`)
          .then(({ data }) => {
            this.classSubjects = data.data
            this.electiveGroupSubjectIds = data.data.elective_subjects.map(e => e.elective_subject_id)
            this.groupSubjectIds = data.data.group_subjects.map(g => g.group_subject_id)
            this.subjectSelectSpinning = false
            resolve()
          })
          .catch(error => {
            this.subjectSelectSpinning = false
            reject(error)
          })
      })
    },

    handleClassChange(classId) {
      this.classId = classId
      this.sectionId = undefined

      this.form.setFieldsValue({
        section_id: undefined,
        subject_id: undefined,
      })

      this.$http.get(`/api/section/class/${classId}`)
        .then(({ data }) => {
          this.sections = data.data
        })

      this.getClassSubjects(classId)
    },

    handleSubjectSelect(value, rowIndex = null) {
      this.teacherSelectSpinning = true
      const params = {
        limit: 10,
        session_id: this.sessionId,
        class_id: this.classId,
        section_id: this.sectionId,
        subject_id: value,
      }

      this.$http.get('/api/staff/subject', { params })
        .then(({ data }) => {
          this.teacherSelectSpinning = false
          this.teachers = data.data

          if (this.isMultiSubject && rowIndex !== null) {
            const fieldData = {}
            fieldData[`subject_teachers[${rowIndex - 1}]teacher_id`] = data.data.staff_id
            const mappedfieldsData = mapValues({ ...fieldData }, function(value) {
              return value !== null ? value : undefined
            })
            this.form.setFieldsValue(mappedfieldsData)
          } else if (!this.isMultiSubject && !this.electiveSubjects.length && !this.groupSubjects.length) {
            this.form.setFieldsValue({
              teacher_id: data.data.staff_id !== null ? data.data.staff_id : undefined,
            })
          }
        })
        .catch(error => {
          this.teacherSelectSpinning = false
          this.$neoNotify.error(error)
        })
    },

    onMultiSubjectChange(e) {
      this.isMultiSubject = e.target.checked
      if (this.isMultiSubject) {
        // Initialize with 2 empty entries if creating new
        if (!this.timetableId) {
          this.multiSubjectEntries = [{}, {}]
        }
        this.electiveSubjects = []
        this.groupSubjects = []
      }
    },

    handleAddMoreSubject() {
      if (this.multiSubjectEntries.length < 6) {
        this.multiSubjectEntries.push({})
      }
    },

    handleRemoveSubject() {
      if (this.multiSubjectEntries.length > 2) {
        this.multiSubjectEntries.pop()
      }
    },

    handleTimePickerOpenChange(open) {
      this.open = open
    },

    handleTimePickerClose() {
      this.open = false
      this.open2 = false
    },

    handleSubmit(e) {
      e.preventDefault()
      this.loading = true

      this.form.validateFields((err, values) => {
        if (!err) {
          this.$confirm({
            title: 'Are you sure?',
            content: 'Click OK to update the timetable entry!',
            onOk: () => {
              const timetableData = this.makeTimetableEntryData(values)

              this.$http
                .patch(`/api/timetable/${this.timetableId}`, timetableData)
                .then(({ data }) => {
                  this.$emit('updated')
                  this.loading = false
                  this.visible = false
                  this.$notification.success({
                    message: 'Successfully updated',
                    description: 'Timetable entry has been updated successfully!',
                  })
                  this.resetForm()
                })
                .catch(error => {
                  console.error(error)
                  this.loading = false
                  this.$neoNotify.error(error)
                })
            },
            onCancel: () => {
              this.loading = false
            },
          })
        } else {
          this.loading = false
        }
      })
    },

    makeTimetableEntryData(values) {
      const data = {
        ...values,
        teacher_id: this.originalData.teacher_id,
        session_id: this.sessionId,
        class_id: this.classId,
        section_id: this.sectionId,
        position: this.position,
        day: this.day,
        start_time: moment(values.start_time).format('HH:mm:ss'),
        end_time: moment(values.end_time).format('HH:mm:ss'),
      }

      if (values.type === 0) {
        if (this.electiveSubjects.length) {
          data.group_type = 1
        } else if (this.groupSubjects.length) {
          data.group_type = 2
        } else if (this.isMultiSubject) {
          data.group_type = 3
        } else {
          data.group_type = 0
        }

        if (this.electiveSubjects.length || this.groupSubjects.length) {
          const subjectTeachers = []
          Object.keys(values.subject_teachers || {}).forEach(key => {
            if (key.includes('teacher_id') && values.subject_teachers[key]) {
              const match = key.match(/subject_teachers\[(\d+)\]teacher_id/)
              if (match) {
                const subjectKey = `subject_teachers[${match[1]}]subject_id`
                if (values.subject_teachers[subjectKey]) {
                  subjectTeachers.push({
                    teacher_id: values.subject_teachers[key],
                    subject_id: values.subject_teachers[subjectKey],
                  })
                }
              }
            }
          })
          data.subject_teachers = subjectTeachers
          data.group_subject_id = values.subject_id
          delete data.subject_id
        }

        if (this.isMultiSubject && values.subject_teachers) {
          const subjectTeachersArray = []
          Object.keys(values.subject_teachers).forEach(key => {
            const match = key.match(/subject_teachers\[(\d+)\](subject_id|teacher_id)/)
            if (match) {
              const index = parseInt(match[1])
              const type = match[2]
              if (!subjectTeachersArray[index]) subjectTeachersArray[index] = {}
              subjectTeachersArray[index][type] = values.subject_teachers[key]
            }
          })

          data.subject_teachers = subjectTeachersArray.filter(item => item && item.subject_id && item.teacher_id)
        }
      } else {
        data.group_type = 0
      }

      return data
    },

    resetForm() {
      this.form.resetFields()
      this.electiveSubjects = []
      this.groupSubjects = []
      this.multiSubjectEntries = []
      this.isMultiSubject = false
      this.timetableId = null
      this.originalData = null
      this.originalGroupType = 0
    },

    handleCancel() {
      this.resetForm()
      this.visible = false
    },
  },
}
</script>
